import "./SharedDevices.scss";

import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import Modal from "react-bootstrap/Modal";
import { useNavigate } from "react-router-dom";
import { MdPeopleOutline, MdClose } from "react-icons/md";

import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import TableColumn from "../../components/TableColumn/TableColumn";
import api from "../../services/api";

// Admin "Shared Devices" tab. Powered by GET /admin/shared-devices, which
// groups accounts by the device_id the mobile app stamps at signup (iOS
// Keychain UUID that survives reinstall / Android ANDROID_ID) and returns any
// physical device tied to 2+ accounts, the core fake-profile signal. This
// screen is read-only: it surfaces the groups so an admin can eyeball a fraud
// ring vs. a legit shared household phone. Actioning happens in the Users tab.
// Deleted and banned accounts are shown here on purpose, flagged inline: they
// used to be filtered out server-side, so deleting one account in a ring hid
// the whole ring from this page.
function SharedDevices() {
  const records = 25;

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // The device group currently open in the drilldown modal (null = closed).
  const [viewing, setViewing] = useState(null);

  const columns = useMemo(
    () => ["Device", "Accounts", "Most Recent Signup", "Who"],
    [],
  );

  const fetchDevices = async (page) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/shared-devices", {
        params: { page, records },
      });
      const list = data?.data || [];
      const formatted = list.map((g) => ({
        _raw: g,
        Device: shortDevice(g.device_id),
        Accounts: accountsLabel(g),
        "Most Recent Signup": safeDate(g.latest_signup),
        Who: previewNames(g.accounts),
      }));
      setRows(formatted);
      // TableColumn wants the RAW record count and divides by `records`
      // itself (same contract as the Users / User Inboxes tabs).
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      console.error("SharedDevices fetch failed:", err);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const actions = useMemo(
    () => [
      {
        label: <MdPeopleOutline size={22} color="#4F30EA" />,
        onClick: (rowData) => setViewing(rowData._raw),
      },
    ],
    [],
  );

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="shared-devices">
            <p className="shared-devices__hint">
              Each row is one physical device that created 2 or more accounts.
              A high count paired with gibberish names or throwaway emails is a
              likely fake-profile ring. Deleted and banned accounts are included
              and flagged. Nothing here is auto-actioned. Open a device to
              review its accounts, then handle them in the Users tab.
            </p>

            <TableColumn
              title="Shared Devices"
              columns={columns}
              data={rows}
              loading={loading}
              totalPages={total}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              records={records}
              actions={actions}
            />
          </section>
        </article>
      </div>

      <DeviceModal group={viewing} onClose={() => setViewing(null)} />
    </main>
  );
}

function DeviceModal({ group, onClose }) {
  const navigate = useNavigate();
  const show = !!group;
  const accounts = group?.accounts || [];

  // Match the Users tab surface the inbox view links to (/users/:mongoId).
  const goToUser = (mongoId) => {
    if (!mongoId) return;
    onClose();
    navigate(`/users/${mongoId}`);
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" scrollable>
      <div className="shared-devices__modal">
        <div className="shared-devices__modal-header">
          <div>
            <div className="shared-devices__modal-title">
              {group?.account_count} accounts on one device
            </div>
            <div className="shared-devices__modal-sub">
              {group ? shortDevice(group.device_id) : ""}
            </div>
          </div>
          <button className="shared-devices__modal-close" onClick={onClose}>
            <MdClose size={22} />
          </button>
        </div>

        <div className="shared-devices__modal-body">
          {accounts.length === 0 ? (
            <div className="shared-devices__empty">No accounts to show.</div>
          ) : (
            accounts.map((a) => (
              <button
                key={a._id}
                className="shared-devices__acct"
                onClick={() => goToUser(a._id)}
                title="Open user profile">
                <span className="shared-devices__acct-name">
                  {[a.firstName, a.lastName].filter(Boolean).join(" ") ||
                    "(no name)"}
                </span>
                <span className="shared-devices__acct-meta">
                  {a.email || "no email"}
                  {a.phone ? ` · ${a.phone}` : ""}
                  {a.deleted_at ? " · DELETED" : ""}
                  {a.banned_at ? " · BANNED" : ""}
                </span>
                <span className="shared-devices__acct-date">
                  {safeDate(a.created_at)}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}

// device_id looks like "ios:<UUID>" or "android:<ANDROID_ID>". Show the
// platform plus a short tail so the row is scannable without leaking the
// whole identifier.
function shortDevice(id) {
  if (!id) return "n/a";
  const [platform, ...rest] = String(id).split(":");
  const val = rest.join(":");
  const tail = (val || String(id)).slice(-8);
  return `${platform} …${tail}`;
}

// "6" when everything is live, "6 (1 deleted)" when the group is only visible
// because deleted/banned members are now counted.
function accountsLabel(g) {
  const bits = [];
  if (g.deleted_count) bits.push(`${g.deleted_count} deleted`);
  if (g.banned_count) bits.push(`${g.banned_count} banned`);
  return bits.length
    ? `${g.account_count} (${bits.join(", ")})`
    : String(g.account_count);
}

function previewNames(accounts) {
  const names = (accounts || [])
    .map((a) => [a.firstName, a.lastName].filter(Boolean).join(" ").trim())
    .filter(Boolean);
  if (names.length === 0) return `${(accounts || []).length} accounts`;
  const head = names.slice(0, 2).join(", ");
  const extra = names.length > 2 ? ` +${names.length - 2}` : "";
  return `${head}${extra}`;
}

// created_at / latest_signup come back as Date.toString() strings; new Date()
// parses them fine in the browser. Guard against anything unparseable.
function safeDate(v) {
  if (!v) return "n/a";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "n/a" : format(d, "MMM d, yyyy HH:mm");
}

export default SharedDevices;
